import { AfterViewInit, Component, ElementRef, inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/legacy-dialog";
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';

import { TimerConfig } from '../../../../core/models/entities/timer-config.type';
import { ColorPickerComponent } from '../../../../core/elements/color-picker/color-picker.component';
import { AppConstants } from '../../../../app.constants';
import { TimerType } from '../../../../core/enums/timer-type.enum';

@Component({
  selector: 'modal-configure-timer',
  standalone: true,
  imports: [
    FormsModule,
    MatCheckboxModule,
    MatButtonModule,
    DragDropModule,
    ColorPickerComponent
  ],
  templateUrl: './modal-configure-timer.component.html',
  styleUrl: './modal-configure-timer.component.scss'
})
export class ModalConfigureTimerComponent implements OnInit, AfterViewInit {
  @ViewChild('monitor', { static: true }) monitor!: ElementRef<HTMLElement>;
  @ViewChild('timer') timer: ElementRef<HTMLElement>;
  @ViewChild('dragHandleCorner') dragHandleCorner: ElementRef<HTMLElement>;

  timerPixelPosition = { x: 0, y: 0 };
  timerTypes = TimerType;

  timerConfig = inject<TimerConfig>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<ModalConfigureTimerComponent>);
  private ngZone = inject(NgZone);

  ngOnInit(): void {
    this.setInitialTimerPosition();
  }

  ngAfterViewInit(): void {
    this.setHandleTransform();
  }

  onDragEnd(event: CdkDragMove<MouseEvent>) {
    const pixel = event.source.getFreeDragPosition();
    const { width, height } = this.monitor.nativeElement.getBoundingClientRect();

    this.timerConfig.PositionX = Math.round((pixel.x / width) * 100);
    this.timerConfig.PositionY = Math.round((pixel.y / height) * 100);

    this.timerPixelPosition = pixel;
  }

  onResizeDragMove(dragHandle: HTMLElement) {
    this.ngZone.runOutsideAngular(() => {
      this.resize(dragHandle, this.timer.nativeElement);
    });
  }

  private setInitialTimerPosition() {
    const { width, height } = this.monitor.nativeElement.getBoundingClientRect();
    const { PositionX, PositionY } = this.timerConfig;

    this.timerPixelPosition = {
      x: Math.round((width * PositionX) / 100),
      y: Math.round((height * PositionY) / 100)
    };
  }

  private resize(dragHandle: HTMLElement, target: HTMLElement) {
    const dragRect = dragHandle.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const parentRect = this.monitor.nativeElement.getBoundingClientRect();

    // calculate new width and height
    let widthPx = dragRect.right - targetRect.left;
    let heightPx = dragRect.bottom - targetRect.top;

    // calculate max available width and height
    const maxWidthPx = parentRect.right - targetRect.left;
    const maxHeightPx = parentRect.bottom - targetRect.top;

    widthPx = Math.min(widthPx, maxWidthPx);
    heightPx = Math.min(heightPx, maxHeightPx);

    // calculate min width and height
    widthPx = Math.max(widthPx, AppConstants.TIMER_MIN_WIDTH);
    heightPx = Math.max(heightPx, AppConstants.TIMER_MIN_WIDTH);

    target.style.width = widthPx + 'px';
    target.style.height = heightPx + 'px';

    // transform px to %
    const { width, height } = this.monitor.nativeElement.getBoundingClientRect();
    this.timerConfig.Width = Math.round((widthPx / width) * 100);
    this.timerConfig.Height = Math.round((heightPx / height) * 100);

    this.setHandleTransform();
  }

  private setHandleTransform() {
    const rect = this.timer.nativeElement.getBoundingClientRect();
    const dragHandle = this.dragHandleCorner.nativeElement;

    const dragRect = dragHandle.getBoundingClientRect();
    const translateX = rect.width - dragRect.width;
    const translateY = rect.height - dragRect.height;

    dragHandle.style.transform = `translate(${translateX}px, ${translateY}px)`;
  }
}
